import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useGuidance } from "../contexts/GuidanceContext";
import { useTheme, type ThemePreference } from "../contexts/ThemeContext";

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string; hint: string }> = [
  { value: "light", label: "Light", hint: "Always light, regardless of system." },
  { value: "dark", label: "Dark", hint: "Always dark, regardless of system." },
  {
    value: "system",
    label: "System",
    hint: "Match your operating system's current preference.",
  },
];

export function SettingsProfile() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { preference, setPreference } = useTheme();
  const { isGuidanceActive, setGuidance } = useGuidance();

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/settings`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Settings
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Profile and preferences
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          These choices are personal and apply to your account everywhere you sign in. They persist
          on the server, so a fresh device picks them up automatically.
        </p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Account</h2>
        <dl className="mt-2 grid gap-1 text-sm">
          <div className="flex gap-2">
            <dt className="text-slate-500 dark:text-slate-400">Email</dt>
            <dd className="text-slate-700 dark:text-slate-200">{user?.email ?? "—"}</dd>
          </div>
          {user?.fullName ? (
            <div className="flex gap-2">
              <dt className="text-slate-500 dark:text-slate-400">Name</dt>
              <dd className="text-slate-700 dark:text-slate-200">{user.fullName}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <fieldset>
          <legend className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Theme
          </legend>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Saved to your account so it follows you to other devices.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {THEME_OPTIONS.map((opt) => {
              const checked = preference === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 transition ${
                    checked
                      ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                      : "border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    name="theme-preference"
                    value={opt.value}
                    checked={checked}
                    onChange={() => setPreference(opt.value)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                      {opt.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                      {opt.hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Coach guidance</h2>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          When on, the Coach surfaces consultant-style hints next to clauses and form fields. Switch
          off if you find them noisy.
        </p>
        <label className="mt-3 inline-flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={isGuidanceActive}
            onChange={(e) => setGuidance(e.target.checked)}
          />
          <span className="text-sm text-slate-700 dark:text-slate-200">
            {isGuidanceActive ? "Coach is on" : "Coach is off"}
          </span>
        </label>
      </section>
    </div>
  );
}
